import { useCallback, useEffect, useState } from "react";
import { AxiosRequestConfig } from "axios";
import { Auth } from "firebase/auth";

import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY, APIHandler } from "../../../api/base";
import { logout } from "../../../api/auth/endpoints-referrers";
import { TokenResponse } from "../../interfaces/auth/token-response.interface";
import { stringGetter } from "../../utilities/localstorage-dealer/localstorage-getters.util";
import {
    setDataToLocalStorage,
    removeDataToLocalStorage
} from "../../utilities/localstorage-dealer/localstorage-setter.util";

interface RetryQueueItem {
    resolve: (value?: any) => void;
    reject: (error?: any) => void;
    config: AxiosRequestConfig;
}

export function useFirebaseToken(auth: Auth, onTokenInvalid: () => void) {
    const [currentAccessToken, setCurrentAccessToken] = useState<string>();

    const getAccessToken = useCallback(() => {
        return stringGetter(ACCESS_TOKEN_KEY);
    }, []);

    const getRefreshToken = useCallback(() => {
        return stringGetter(REFRESH_TOKEN_KEY);
    }, []);

    const isAuthenticated = useCallback(() => {
        return !!getAccessToken() && !!getRefreshToken();
    }, [getAccessToken, getRefreshToken]);

    const setAccessToken = useCallback((token: string) => {
        setDataToLocalStorage(ACCESS_TOKEN_KEY, token);
        setCurrentAccessToken(token);
    }, []);

    const setTokens = useCallback(({ accessToken, refreshToken }: TokenResponse) => {
        setDataToLocalStorage(ACCESS_TOKEN_KEY, accessToken);
        setDataToLocalStorage(REFRESH_TOKEN_KEY, refreshToken);
        setCurrentAccessToken(accessToken);
    }, []);

    const clearToken = useCallback(
        async (shouldClearSession = false) => {
            const clearSession =
                shouldClearSession && isAuthenticated() ? logout() : Promise.resolve();
            try {
                return await clearSession;
            } finally {
                removeDataToLocalStorage(ACCESS_TOKEN_KEY);
                removeDataToLocalStorage(REFRESH_TOKEN_KEY);
                setCurrentAccessToken("");
                onTokenInvalid();
            }
        },
        [onTokenInvalid, isAuthenticated]
    );

    const updateCurrentAccessToken = useCallback(() => {
        setCurrentAccessToken(getAccessToken() ?? undefined);
    }, [getAccessToken]);

    useEffect(() => {
        updateCurrentAccessToken();
    }, [updateCurrentAccessToken]);

    useEffect(() => {
        const retryQueueList: RetryQueueItem[] = [];
        let isRefreshing = false;

        APIHandler.interceptors.response.use(
            response => response,
            async error => {
                if (
                    error.response &&
                    error.response.status === 401 &&
                    auth.currentUser &&
                    isAuthenticated()
                ) {
                    const originalRequest: AxiosRequestConfig = error.config;
                    if (!isRefreshing) {
                        isRefreshing = true;
                        try {
                            const accessToken = await auth.currentUser.getIdToken(true);
                            const refreshToken = auth.currentUser.refreshToken;
                            setTokens({ accessToken, refreshToken });

                            retryQueueList.forEach(({ config, resolve, reject }) => {
                                APIHandler.request(config)
                                    .then(response => resolve(response))
                                    .catch(error => reject(error));
                            });

                            retryQueueList.length = 0;

                            return APIHandler(originalRequest);
                        } catch (refreshError) {
                            clearToken();
                            throw refreshError;
                        } finally {
                            isRefreshing = false;
                        }
                    }
                    return new Promise<void>((resolve, reject) => {
                        retryQueueList.push({
                            config: originalRequest,
                            resolve,
                            reject
                        });
                    });
                }

                return Promise.reject(error);
            }
        );
    }, [onTokenInvalid, isAuthenticated, clearToken, setTokens, auth]);

    return {
        currentAccessToken,
        isAuthenticated,
        setAccessToken,
        setTokens,
        clearToken,
        updateCurrentAccessToken
    };
}
